/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import React, { useState, useMemo, useContext, useEffect, useCallback, useRef } from 'react';
import type { FC } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { useQuery } from '@apollo/react-hooks';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { isErrorMarkedAsHandled, markErrorAsHandled } from '@confluence/graphql';
import { SPAViewContext } from '@confluence/spa-view-context';

import { PremiumFeatureDeeplinks } from '../PremiumTrialOnboarding/PremiumFeatureDeeplinks';

import type { AutomationPremiumTipQuery as AutomationPremiumTipQuery$data } from './__types__/AutomationPremiumTipQuery';
import { PremiumTipPromptSlideIn } from './PremiumTipPromptSlideIn';
import { usePremiumTipActions } from './PremiumTipStore';
import { PremiumMessagesCadenceState, PremiumMessages } from './PremiumMessagesCadenceState';
import { useEligibilityBiteSizedPremiumMessages } from './useEligibilityBiteSizedPremiumMessages';
import { PremiumTipType } from './PremiumTipType';

const i18n = defineMessages({
	// cadence 1
	titleAutomationMessageCadence1: {
		id: 'premium-trial.premium-tip-prompt.automation.first.title',
		defaultMessage: 'Save time and focus on what matters',
		description: 'Title for Premium tip prompt of automation flow on cadence 1',
	},
	descriptionAutomationCadence1: {
		id: 'premium-trial.premium-tip-prompt.automation.first.description',
		defaultMessage:
			'Eliminate repetitive tasks such as label creation, reminders, and more with automation.',
		description: 'Description for Premium tip prompt of automation flow on cadence 1',
	},
	// cadence 2
	titleAutomationMessageCadence2: {
		id: 'premium-trial.premium-tip-prompt.automation.second.title',
		defaultMessage: 'You’re a few steps away from peak productivity',
		description: 'Title for Premium tip prompt of automation flow on cadence 2',
	},
	descriptionAutomationCadence2: {
		id: 'premium-trial.premium-tip-prompt.automation.second.description',
		defaultMessage: 'Simplify your workflow by using AI to create automation rules for you.',
		description: 'Description for Premium tip prompt of automation flow on cadence 2',
	},
	// cadence 3
	titleAutomationMessageCadence3: {
		id: 'premium-trial.premium-tip-prompt.automation.third.title',
		defaultMessage: 'Don’t lift a finger with automation',
		description: 'Title for Premium tip prompt of automation flow on cadence 3',
	},
	descriptionAutomationCadence3: {
		id: 'premium-trial.premium-tip-prompt.automation.third.description',
		defaultMessage:
			'Create automation rules to help you work smarter so you can focus on what really matters.',
		description: 'Description for Premium tip prompt of automation flow on cadence 3',
	},
	// cadence 4
	titleAutomationMessageCadence4: {
		id: 'premium-trial.premium-tip-prompt.automation.fourth.title',
		defaultMessage: 'Make automation work for you',
		description: 'Title for Premium tip prompt of automation flow on cadence 4',
	},
	descriptionAutomationCadence4: {
		id: 'premium-trial.premium-tip-prompt.automation.fourth.description',
		defaultMessage:
			'Did you know you can set reminders, archive inactive content, and more with automation?',
		description: 'Description for Premium tip prompt of automation flow on cadence 4',
	},
	// cadence 5
	titleAutomationMessageCadence5: {
		id: 'premium-trial.premium-tip-prompt.automation.fifth.title',
		defaultMessage: 'Simplify your daily to-do list',
		description: 'Title for Premium tip prompt of automation flow on cadence 5',
	},
	descriptionAutomationCadence5: {
		id: 'premium-trial.premium-tip-prompt.automation.fifth.description',
		defaultMessage:
			'Create rules to help you seamlessly collaborate, organize content, and work across multiple tools.',
		description: 'Description for Premium tip prompt of automation flow on cadence 5',
	},
	// CTAs
	automationCTAOpenAutomation: {
		id: 'premium-trial.premium-tip-prompt.automation.action-open-automation',
		defaultMessage: 'Go to automation',
		description: 'CTA to Open PTO Modal for Premium tip prompt of generic flow',
	},
	automationCTADismissMessage: {
		id: 'premium-trial.premium-tip-prompt.automation.action-dismiss-message',
		defaultMessage: 'Don’t show this again',
		description: 'CTA to Dismiss Premium tip prompt of automation flow',
	},
});

const MESSAGE_CADENCE = [
	{
		title: i18n.titleAutomationMessageCadence1,
		description: i18n.descriptionAutomationCadence1,
	},
	{
		title: i18n.titleAutomationMessageCadence2,
		description: i18n.descriptionAutomationCadence2,
	},
	{
		title: i18n.titleAutomationMessageCadence3,
		description: i18n.descriptionAutomationCadence3,
	},
	{
		title: i18n.titleAutomationMessageCadence4,
		description: i18n.descriptionAutomationCadence4,
	},
	{
		title: i18n.titleAutomationMessageCadence5,
		description: i18n.descriptionAutomationCadence5,
	},
];

export enum AutomationPremiumTipSource {
	PAGE_PUBLISH = 'pagePublish',
	RESTRICTIONS_DIALOG = 'restrictionsDialog',
}

export const AutomationPremiumTip: FC<{ source: AutomationPremiumTipSource | undefined }> = ({
	source,
}) => {
	const { daysLeft } = useEligibilityBiteSizedPremiumMessages();

	const { getCadence, shouldShowMessage, setMessageNextCadence, dismissMessage } = useMemo(() => {
		const genericMessagesCadenceState = new PremiumMessagesCadenceState(PremiumMessages.Automation);
		return {
			getCadence: genericMessagesCadenceState.getMessageCadence,
			dismissMessage: genericMessagesCadenceState.dismissMessage,
			shouldShowMessage: genericMessagesCadenceState.canShowMessage,
			setMessageNextCadence: genericMessagesCadenceState.setMessageNextCadence,
		};
	}, []);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const { isSiteAdmin } = useContext(SPAViewContext);
	const [shouldShow, setShouldShow] = useState<boolean>(false);
	const [cadence, setCadence] = useState(-1);
	const shouldShowSecondaryCTA = cadence > 1;
	const isAanalyticFired = useRef(false);
	const { closePrompt } = usePremiumTipActions();

	// set next cadence as soon as the message is shown
	useEffect(() => {
		if (shouldShow) {
			setMessageNextCadence();
		}
	}, [shouldShow, setMessageNextCadence]);

	// prompt trigger
	useEffect(() => {
		const show = shouldShowMessage();
		if (source === AutomationPremiumTipSource.PAGE_PUBLISH && show) {
			const timeout = setTimeout(() => {
				setShouldShow(true);
				setCadence(getCadence());
			}, 1000);
			return () => clearTimeout(timeout);
		} else {
			setShouldShow(show);
			setCadence(getCadence());
		}
	}, [source, shouldShowMessage, getCadence]);

	// update sweetstate when the component is unmounted
	useEffect(() => {
		return () => {
			void closePrompt();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (shouldShow && isAanalyticFired.current === false) {
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: 'premiumTipPrompt',
					attributes: {
						cadence,
						type: PremiumTipType.AUTOMATION,
						daysLeft,
						triggerSource: source,
					},
				},
			}).fire();
			isAanalyticFired.current = true;
		}
	}, [createAnalyticsEvent, cadence, shouldShow, source, daysLeft]);

	const { data, loading, error } = useQuery<AutomationPremiumTipQuery$data>(
		gql`
			query AutomationPremiumTipQuery {
				user: confluence_atlassianUser(current: true) {
					id
					confluence {
						hasPersonalSpace
						space {
							id
							alias
						}
					}
				}
			}
		`,
		{
			skip: !shouldShow,
		},
	);
	const personalSpaceAlias =
		(data?.user?.confluence?.hasPersonalSpace && data?.user?.confluence?.space?.alias) || undefined;

	const automationDeeplink = useMemo<string>(() => {
		const premiumFeatureDeeplinks = new PremiumFeatureDeeplinks({
			personalSpaceKey: personalSpaceAlias,
		});

		return isSiteAdmin
			? premiumFeatureDeeplinks.getAutomationAdmin()
			: premiumFeatureDeeplinks.getAutomationEndUser();
	}, [personalSpaceAlias, isSiteAdmin]);

	const onClose = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'premiumTipPromptCloseButton',
				source: `${PremiumTipType.AUTOMATION}PremiumTipPrompt`,
				attributes: {
					cadence,
					daysLeft,
					triggerSource: source,
				},
			},
		}).fire();

		setShouldShow(false);
		void closePrompt();
	}, [closePrompt, createAnalyticsEvent, cadence, daysLeft, source]);

	const onPrimaryCtaClick = useCallback(() => {
		setShouldShow(false);
		dismissMessage();

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'goToAutomationButton',
				source: `${PremiumTipType.AUTOMATION}PremiumTipPrompt`,
				attributes: {
					cadence,
					daysLeft,
					triggerSource: source,
				},
			},
		}).fire();
	}, [dismissMessage, createAnalyticsEvent, cadence, daysLeft, source]);

	const onSecondaryCtaClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'dismissForeverButton',
				source: `${PremiumTipType.AUTOMATION}PremiumTipPrompt`,
				attributes: {
					cadence,
					daysLeft,
					triggerSource: source,
				},
			},
		}).fire();

		setShouldShow(false);
		dismissMessage();
		void closePrompt();
	}, [dismissMessage, closePrompt, cadence, createAnalyticsEvent, source, daysLeft]);

	if (error && !isErrorMarkedAsHandled(error)) {
		markErrorAsHandled(error);
	}

	if (loading) {
		return null;
	}

	return (
		<>
			<PremiumTipPromptSlideIn
				shouldShow={shouldShow}
				title={cadence < 1 ? '' : formatMessage(MESSAGE_CADENCE[cadence - 1].title)}
				description={cadence < 1 ? '' : formatMessage(MESSAGE_CADENCE[cadence - 1].description)}
				onClose={onClose}
				primaryAction={{
					content: formatMessage(i18n.automationCTAOpenAutomation),
					onClick: onPrimaryCtaClick,
					link: {
						href: automationDeeplink,
						// open the link in a new tab if it's fallback link that is external
						target: automationDeeplink.startsWith('https') ? '_blank' : undefined,
					},
				}}
				secondaryAction={
					shouldShowSecondaryCTA
						? {
								content: formatMessage(i18n.automationCTADismissMessage),
								onClick: onSecondaryCtaClick,
							}
						: undefined
				}
			/>
		</>
	);
};
